import React from "react";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Icon from "@/components/ui/Icon";
import PageHeader from "@/components/ui/PageHeader";
import Skeleton from "@/components/ui/Skeleton";
import { FEATURE_KICKER, LOCKED_FEATURES, LockedFeatureKey } from "@/constants/lockedFeatures";
import { useSubscription } from "@/hooks/useSubscription";
import { useUserRoles } from "@/hooks/useUserRoles";
import { PATHS } from "@/routes/paths";
import { SubscriptionFeature, USER_ROLES } from "@/types/auth";
import styles from "./styles.module.scss";

interface FeatureGateProps {
  feature: SubscriptionFeature;
  copy: LockedFeatureKey;
  children: React.ReactNode;
}

export const hasFeature = (features: string[] | undefined, feature: SubscriptionFeature) =>
  (features ?? []).some((f) => f.toLowerCase().trim() === feature);

// Bloqueia uma página quando o plano não inclui a funcionalidade: prévia
// desfocada + cartão com benefícios. Perfis que não compram veem um aviso.
const FeatureGate: React.FC<FeatureGateProps> = ({ feature, copy, children }) => {
  const { subscription, loading } = useSubscription();
  const { roles, canManageBilling } = useUserRoles();

  if (loading && !subscription) return <Skeleton height={420} radius={18} />;
  if (hasFeature(subscription?.features, feature)) return <>{children}</>;

  const { title, lead, points } = LOCKED_FEATURES[copy];
  const role = roles[0];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={title}
        description={lead}
        badge={
          <Badge tone="accentSoft" icon="lock">
            Bloqueado no seu plano
          </Badge>
        }
      />

      <div className={styles.preview} aria-hidden="true">
        <div className={styles.previewKpis}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.previewCard}>
              <span />
              <b />
            </div>
          ))}
        </div>
        <div className={styles.previewChart} />
        <div className={styles.fade} />
      </div>

      <section className={styles.card} aria-labelledby="feature-gate-title">
        <div className={styles.cardHeader}>
          <span className={styles.lockIcon}>
            <Icon name="lock" size={24} />
          </span>
          <div>
            <span className={styles.kicker}>{FEATURE_KICKER[feature]}</span>
            <h2 id="feature-gate-title" className={styles.cardTitle}>
              Desbloqueie {title}
            </h2>
          </div>
        </div>

        <ul className={styles.points}>
          {points.map((point) => (
            <li key={point}>
              <span className={styles.check}>
                <Icon name="check" size={12} strokeWidth={3} />
              </span>
              {point}
            </li>
          ))}
        </ul>

        {subscription?.name && (
          <div className={styles.plan}>
            <Icon name="star" size={16} strokeWidth={2.2} />
            <span>
              Seu plano atual: <b>{subscription.name}</b>.
            </span>
          </div>
        )}

        {canManageBilling ? (
          <div className={styles.actions}>
            <Button to={PATHS.plans} size="lg" icon="lock" block>
              Desbloquear funcionalidade
            </Button>
            <Button to={PATHS.subscription} size="lg" variant="secondary">
              Ver meu plano
            </Button>
          </div>
        ) : (
          <div className={styles.notice} role="note">
            <Icon name="info" size={18} strokeWidth={2.2} />
            <span>
              Seu perfil{role ? <> (<b>{USER_ROLES[role]}</b>)</> : null} não pode contratar planos.
              Peça a um <b>Administrador</b> ou ao <b>Financeiro</b> da conta para desbloquear esta
              funcionalidade.
            </span>
          </div>
        )}
      </section>
    </div>
  );
};

export default FeatureGate;
